import { useEffect, useRef, useState, useCallback } from "react";
import { sfx } from "../../lib/soundEngine";

const W = 340;
const H = 460;
const SHIP_W = 30;
const SHIP_H = 22;
const SHIP_Y = H - 40;
const OBS_W = 30;
const OBS_H = 20;

export const NeonDodgeGame = () => {
    const canvasRef = useRef(null);
    const rafRef = useRef(0);
    const stateRef = useRef({
        shipX: W / 2,
        vx: 0,
        input: 0, // -1 left, +1 right, 0 idle
        obstacles: [],
        spawnCooldown: 40,
        frame: 0,
        speed: 2.4,
        alive: true,
        score: 0,
    });
    const [running, setRunning] = useState(false);
    const [gameOver, setGameOver] = useState(false);
    const [score, setScore] = useState(0);
    const [best, setBest] = useState(0);

    const setInput = useCallback((dir) => {
        stateRef.current.input = dir;
    }, []);

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        const s = stateRef.current;
        ctx.fillStyle = "#0d0d18";
        ctx.fillRect(0, 0, W, H);

        // scrolling grid backdrop
        const off = (s.frame * s.speed) % 40;
        ctx.strokeStyle = "rgba(43, 27, 84, 0.55)";
        ctx.lineWidth = 1;
        for (let y = -40 + off; y <= H; y += 40) {
            ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke();
        }
        for (let x = 0; x <= W; x += 40) {
            ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke();
        }

        // obstacles
        s.obstacles.forEach((o) => {
            ctx.fillStyle = "#ff007f";
            ctx.shadowColor = "#ff007f";
            ctx.shadowBlur = 12;
            ctx.fillRect(o.x, o.y, OBS_W, OBS_H);
        });
        ctx.shadowBlur = 0;

        // ship (triangle)
        ctx.fillStyle = "#00f0ff";
        ctx.shadowColor = "#00f0ff";
        ctx.shadowBlur = 14;
        ctx.beginPath();
        ctx.moveTo(s.shipX, SHIP_Y);
        ctx.lineTo(s.shipX - SHIP_W / 2, SHIP_Y + SHIP_H);
        ctx.lineTo(s.shipX + SHIP_W / 2, SHIP_Y + SHIP_H);
        ctx.closePath();
        ctx.fill();
        ctx.shadowBlur = 0;
    }, []);

    const step = useCallback(() => {
        const s = stateRef.current;
        if (!s.alive) return;
        s.frame += 1;

        // move ship
        s.vx = s.input * 4;
        s.shipX = Math.max(SHIP_W / 2, Math.min(W - SHIP_W / 2, s.shipX + s.vx));

        // spawn obstacle
        s.spawnCooldown -= 1;
        if (s.spawnCooldown <= 0) {
            const x = Math.random() * (W - OBS_W);
            s.obstacles.push({ x, y: -OBS_H });
            const min = Math.max(14, 42 - Math.floor(s.frame / 240));
            s.spawnCooldown = min + Math.floor(Math.random() * 12);
        }

        // move obstacles
        s.obstacles.forEach((o) => { o.y += s.speed; });
        s.obstacles = s.obstacles.filter((o) => {
            if (o.y > H) {
                s.score += 1;
                setScore(s.score);
                return false;
            }
            return true;
        });

        // speed ramp
        if (s.frame % 300 === 0) s.speed = Math.min(6, s.speed + 0.3);

        // collision (AABB vs triangle ~ bounding box)
        const shipLeft = s.shipX - SHIP_W / 2;
        const shipTop = SHIP_Y;
        for (const o of s.obstacles) {
            if (
                o.x < shipLeft + SHIP_W &&
                o.x + OBS_W > shipLeft &&
                o.y < shipTop + SHIP_H &&
                o.y + OBS_H > shipTop
            ) {
                s.alive = false;
                setRunning(false);
                setGameOver(true);
                sfx.gameOver();
                setBest((prev) => (s.score > prev ? s.score : prev));
                draw();
                return;
            }
        }

        draw();
        rafRef.current = requestAnimationFrame(step);
    }, [draw]);

    const start = useCallback(() => {
        stateRef.current = {
            shipX: W / 2,
            vx: 0,
            input: 0,
            obstacles: [],
            spawnCooldown: 30,
            frame: 0,
            speed: 2.4,
            alive: true,
            score: 0,
        };
        setScore(0);
        setGameOver(false);
        setRunning(true);
        sfx.coin();
    }, []);

    useEffect(() => {
        draw();
        return () => cancelAnimationFrame(rafRef.current);
    }, [draw]);

    useEffect(() => {
        if (!running) return;
        rafRef.current = requestAnimationFrame(step);
        return () => cancelAnimationFrame(rafRef.current);
    }, [running, step]);

    useEffect(() => {
        const down = (e) => {
            const k = e.key.toLowerCase();
            if (["arrowleft", "a"].includes(k)) { setInput(-1); e.preventDefault(); }
            else if (["arrowright", "d"].includes(k)) { setInput(1); e.preventDefault(); }
        };
        const up = (e) => {
            const k = e.key.toLowerCase();
            if (["arrowleft", "a", "arrowright", "d"].includes(k)) setInput(0);
        };
        window.addEventListener("keydown", down);
        window.addEventListener("keyup", up);
        return () => {
            window.removeEventListener("keydown", down);
            window.removeEventListener("keyup", up);
        };
    }, [setInput]);

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between font-pixel text-lg">
                <span className="neon-cyan" data-testid="dodge-score">SCORE: {score}</span>
                <span className="neon-amber" data-testid="dodge-best">BEST: {best}</span>
            </div>

            <div className="relative mx-auto" style={{ width: "100%", maxWidth: W }}>
                <canvas
                    ref={canvasRef}
                    width={W}
                    height={H}
                    data-testid="dodge-canvas"
                    className="w-full h-auto rounded-md border-2"
                    style={{ borderColor: "var(--neon-cyan)", boxShadow: "0 0 18px rgba(0,240,255,0.35)" }}
                />
                {(!running || gameOver) && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/75 rounded-md">
                        <p className="font-pixel text-3xl neon-cyan mb-1">
                            {gameOver ? "SHIP DESTROYED" : "READY?"}
                        </p>
                        {gameOver && (
                            <p className="font-pixel text-lg neon-amber mb-3">
                                Dodged: {score}
                            </p>
                        )}
                        <button onClick={start} data-testid="dodge-start-btn" className="btn-neon">
                            <i className="fa-solid fa-play" /> {gameOver ? "Retry" : "Start"}
                        </button>
                    </div>
                )}
            </div>

            <div className="flex justify-center gap-4 mt-1">
                <button
                    aria-label="Move left"
                    data-testid="dodge-btn-left"
                    className="dpad-btn"
                    onMouseDown={() => setInput(-1)}
                    onMouseUp={() => setInput(0)}
                    onMouseLeave={() => setInput(0)}
                    onTouchStart={(e) => { e.preventDefault(); setInput(-1); }}
                    onTouchEnd={() => setInput(0)}
                >
                    <i className="fa-solid fa-caret-left" />
                </button>
                <button
                    aria-label="Move right"
                    data-testid="dodge-btn-right"
                    className="dpad-btn"
                    onMouseDown={() => setInput(1)}
                    onMouseUp={() => setInput(0)}
                    onMouseLeave={() => setInput(0)}
                    onTouchStart={(e) => { e.preventDefault(); setInput(1); }}
                    onTouchEnd={() => setInput(0)}
                >
                    <i className="fa-solid fa-caret-right" />
                </button>
            </div>
            <p className="text-xs text-gray-500 text-center font-body">
                Dodge the pink meteors. Arrow keys / A · D or hold the buttons. Speed ramps every few seconds.
            </p>
        </div>
    );
};
